import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { onUnmounted, ref } from "vue";
import { ExpressionValidationHubMessage, FormElementBase } from "~/api";
import { FormElementMappedToNumberType } from "~/NewFormBuilder/composables/useFormResponse/types";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";

/**
 * Composable for validating mathematical expressions in form elements
 *
 * This composable provides real-time validation of mathematical expressions
 * by connecting to a SignalR hub that performs server-side validation.
 * It handles connection management, expression validation requests, and
 * result processing.
 *
 * @returns {Object} Functions and reactive state for expression validation
 */
export function useExpressionValidator() {
  // Reactive state for tracking connection status and validation results
  const isConnected = ref(false);
  const validationResult = ref<ExpressionValidationHubMessage>();

  // SignalR connection instance
  let connection: HubConnection | null = null;

  /**
   * Establishes a connection to the expression validator SignalR hub
   *
   * Sets up event handlers for connection close and reconnection events
   * to keep the connection status in sync with the actual hub state.
   *
   * @returns {Promise<void>}
   */
  const startConnection = async () => {
    try {
      // Get or create a hub connection through the hub manager
      connection = await hubManager.getHub(
        "expressionValidator",
        "/expressionValidator",
      );

      // Handle connection close events
      connection?.onclose((error) => {
        isConnected.value = false;
        console.warn("SignalR connection closed:", error);
      });

      // Handle successful reconnection events
      connection?.onreconnected(() => {
        isConnected.value = true;
      });
    } catch (err) {
      showError("SignalR start error:", err);
    }
  };

  /**
   * Validates a mathematical expression using the server-side validator
   *
   * Sends the expression, available question codes, and form element types
   * to the server for validation and returns the validation result.
   *
   * @param {string} expression - The mathematical expression to validate
   * @param {string[]} possibleQuestionCodes - Available question codes that can be used in the expression
   * @param {FormElementBase[]} incomingFormElements - Form elements with their types
   * @returns {Promise<ExpressionValidationHubMessage>} Validation result from the server
   */
  const validateExpression = async (
    expression: string,
    possibleQuestionCodes: string[],
    incomingFormElements: FormElementBase[],
  ) => {
    // Convert form elements to the format expected by the server
    // Maps string element types to their numeric equivalents
    const formElements = incomingFormElements.map((el) => ({
      questionCode: el.questionCode,
      type: FormElementMappedToNumberType[
        el.type as keyof typeof FormElementMappedToNumberType
      ], // converts string to number
    }));

    // Ensure the connection is ready before sending data
    if (!connection || connection.state !== HubConnectionState.Connected) {
      console.warn(
        "Connection is not ready. Current state:",
        connection?.state,
      );

      // Attempt to start the connection if not ready
      await startConnection();
    }

    // If the connection is ready, try the expression validation
    if (connection?.state === HubConnectionState.Connected) {
      return new Promise<ExpressionValidationHubMessage>((resolve, reject) => {
        // Create a handler for the validation response
        const handler = (data: ExpressionValidationHubMessage) => {
          // Store the result in reactive state
          validationResult.value = data;
          // Remove the handler to prevent memory leaks
          connection?.off("ExpressionValidated", handler);
          // Resolve the promise with the validation result
          resolve(data);
        };

        // Register the handler for the "ExpressionValidated" event
        connection?.on("ExpressionValidated", handler);

        // Invoke the server method to validate the expression
        connection
          ?.invoke(
            "ValidateExpression",
            expression,
            possibleQuestionCodes,
            formElements,
          )
          .catch((err) => {
            // Clean up the handler if the invoke fails
            connection?.off("ExpressionValidated", handler);
            showError("SignalR invoke error:", err);
            reject(err);
          });
      });
    } else {
      console.warn("Connection could not be established. Aborting validation.");
      return Promise.reject("Connection not ready");
    }
  };

  /**
   * Stops the SignalR connection to the expression validator hub
   *
   * @returns {Promise<void>}
   */
  const stopConnection = async () => {
    if (connection && connection.state === HubConnectionState.Connected) {
      await hubManager.stop("expressionValidator");
      isConnected.value = false;
    }
  };

  // Clean up the connection when the component is unmounted
  onUnmounted(async () => {
    await stopConnection();
  });

  // Return the public API of the composable
  return {
    isConnected, // Reactive connection status
    validationResult, // Reactive validation result
    validateExpression, // Function to validate expressions
    stopConnection, // Function to manually stop the connection
  };
}
